import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Users, Trophy, Settings, Trash2 } from 'lucide-react'
import roomsService from '../services/roomsService'
import golfRoomSettingsService from '../services/golfRoomSettingsService'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { canManageRoom } from '../utils/roomPermissions'
import { RootState } from '../store'
import DeleteRoomModal from '../components/DeleteRoomModal'

const GolfRoom: React.FC = () => {
  const { t } = useTranslation()
  const { roomId } = useParams<{ roomId: string }>()
  const { user } = useSelector((state: RootState) => state.auth)
  const [showDeleteRoomModal, setShowDeleteRoomModal] = useState(false)

  const { data: room, isLoading: roomLoading } = useQuery({
    queryKey: ['golf-room-summary', roomId],
    queryFn: () => roomsService.getRoomSummary(roomId!),
    enabled: !!roomId,
  })

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['golf-room-settings', roomId],
    queryFn: () => golfRoomSettingsService.getRoomSettings(roomId!),
    enabled: !!roomId,
  })

  if (!roomId) return null

  return (
    <div className="min-h-screen bg-background pt-16 sm:pt-20 pb-10">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="glass-card rounded-2xl p-5 sm:p-6 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="font-display text-display-2 font-bold text-foreground">
                ⛳ {room?.name || 'Golf room'}
              </h1>
              <p className="text-muted-foreground mt-1">
                Golf room dashboard with settings and ranking overview.
              </p>
              {canManageRoom(room, user?._id) && roomId && (
                <button
                  type="button"
                  onClick={() => setShowDeleteRoomModal(true)}
                  className="inline-flex items-center gap-1 mt-2 text-xs text-destructive/90 hover:text-destructive"
                >
                  <Trash2 size={12} />
                  {t('room.delete.action')}
                </button>
              )}
            </div>
            <Link
              to={`/golf`}
              className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Open golf demo
            </Link>
            <Link
              to={`/golf/${roomId}/play`}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Play golf
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="glass-card rounded-2xl p-4 sm:p-5 lg:col-span-2">
            <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              Rankings
            </h2>
            {roomLoading ? (
              <p className="text-sm text-muted-foreground">Loading room...</p>
            ) : (
              <div className="space-y-2">
                {(room?.players || []).map((player, index) => (
                  <div key={player._id} className="rounded-xl bg-muted/30 border border-border/50 p-3 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground">#{index + 1} {player.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatPoints(player.totalPoints ?? 0, t)}
                      </p>
                    </div>
                    <Users className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
                {(room?.players || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No players yet.</p>
                )}
              </div>
            )}
          </div>

          <div className="glass-card rounded-2xl p-4 sm:p-5">
            <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Scoring settings
            </h2>
            {settingsLoading ? (
              <p className="text-sm text-muted-foreground">Loading settings...</p>
            ) : settings ? (
              <div className="space-y-1.5 text-sm text-muted-foreground">
                <p>Hole-in-one: <span className="text-foreground font-medium">{settings.holeInOnePoints}</span></p>
                <p>Albatross: <span className="text-foreground font-medium">{settings.albatrossPoints}</span></p>
                <p>Eagle: <span className="text-foreground font-medium">{settings.eaglePoints}</span></p>
                <p>Birdie: <span className="text-foreground font-medium">{settings.birdiePoints}</span></p>
                <p>Par: <span className="text-foreground font-medium">{settings.parPoints}</span></p>
                <p>Bogey: <span className="text-foreground font-medium">{settings.bogeyPoints}</span></p>
                <p>Double bogey: <span className="text-foreground font-medium">{settings.doubleBogeyPoints}</span></p>
                <p>Fairway hit: <span className="text-foreground font-medium">{settings.fairwayHitPoints}</span></p>
                <p>Longest drive bonus: <span className="text-foreground font-medium">{settings.longestDriveBonus}</span></p>
                <p>Closest to pin bonus: <span className="text-foreground font-medium">{settings.closestToPinBonus}</span></p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Settings unavailable.</p>
            )}
          </div>
        </div>
      </div>
      {showDeleteRoomModal && roomId && (
        <DeleteRoomModal
          roomId={roomId}
          roomName={room?.name}
          onClose={() => setShowDeleteRoomModal(false)}
        />
      )}
    </div>
  )
}

export default GolfRoom

